import { Box, Typography, Paper, List, ListItem, ListItemText, Chip, Skeleton, useMediaQuery, useTheme } from '@mui/material'
import { PlanningOverviewDto } from '@/core/models/planning.model'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

interface CriticalAttentionSectionProps {
  plannings: PlanningOverviewDto[]
  isLoading: boolean
}

export function CriticalAttentionSection({ plannings, isLoading }: CriticalAttentionSectionProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const highAttentionPlannings = plannings.filter((p) => p.attentionLevel === 'high')
  const mediumAttentionPlannings = plannings.filter((p) => p.attentionLevel === 'medium')

  if (isLoading) {
    return (
      <Paper sx={{ p: { xs: 1.5, md: 3 }, mb: { xs: 1.5, md: 4 } }}>
        <Typography variant={isMobile ? 'subtitle1' : 'h5'} gutterBottom>
          {t('page.planning.dashboard.attention.title', { defaultValue: 'Plannings Needing Attention' })}
        </Typography>
        <Skeleton variant="rectangular" width="100%" height={isMobile ? 96 : 200} />
      </Paper>
    )
  }

  if (highAttentionPlannings.length === 0 && mediumAttentionPlannings.length === 0) {
    if (isMobile) return null

    return (
      <Paper sx={{ p: 3, mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          {t('page.planning.dashboard.attention.allGood', { defaultValue: 'All Plannings On Track' })}
        </Typography>
        <Typography color="text.secondary">
          {t('page.planning.dashboard.attention.noAttention', { defaultValue: 'No plannings currently need attention. Great job!' })}
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: { xs: 1.5, md: 3 }, mb: { xs: 1.5, md: 4 } }}>
      <Typography variant={isMobile ? 'subtitle1' : 'h5'} gutterBottom>
        {t('page.planning.dashboard.attention.title', { defaultValue: 'Plannings Needing Attention' })}
      </Typography>

      {highAttentionPlannings.length > 0 && (
        <>
          <Typography variant="subtitle1" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <ErrorIcon /> {t('page.planning.dashboard.attention.highPriority', { defaultValue: 'High Priority' })}
          </Typography>
          <List dense={isMobile}>
            {highAttentionPlannings.map((planning) => (
              <AttentionListItem key={planning._id} planning={planning} />
            ))}
          </List>
        </>
      )}

      {mediumAttentionPlannings.length > 0 && (
        <>
          <Typography variant="subtitle1" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <WarningIcon /> {t('page.planning.dashboard.attention.mediumPriority', { defaultValue: 'Medium Priority' })}
          </Typography>
          <List dense={isMobile}>
            {mediumAttentionPlannings.map((planning) => (
              <AttentionListItem key={planning._id} planning={planning} />
            ))}
          </List>
        </>
      )}
    </Paper>
  )
}

interface AttentionListItemProps {
  planning: PlanningOverviewDto
}

function AttentionListItem({ planning }: AttentionListItemProps) {
  const { t } = useTranslation()
  let reason = ''
  let timeInfo = ''

  if (planning.status === 'upcoming') {
    timeInfo = t('page.planning.dashboard.attention.startsIn', {
      days: planning.daysUntilStart,
      defaultValue: `Starts in ${planning.daysUntilStart} days`,
    })

    reason =
      planning.completionRate < 50
        ? t('page.planning.dashboard.attention.lowCompletion', {
            rate: Math.round(planning.completionRate),
            staffed: planning.staffedShifts,
            total: planning.totalPossibleShifts,
            defaultValue: `Only ${Math.round(planning.completionRate)}% of shifts are staffed (${planning.staffedShifts}/${planning.totalPossibleShifts})`,
          })
        : t('page.planning.dashboard.attention.needsMoreStaff', {
            staffed: planning.staffedShifts,
            total: planning.totalPossibleShifts,
            defaultValue: `Needs more staff assigned (${planning.staffedShifts}/${planning.totalPossibleShifts})`,
          })
  } else if (planning.status === 'active') {
    timeInfo = t('page.planning.dashboard.attention.daysRemaining', {
      days: planning.daysRemaining,
      defaultValue: `${planning.daysRemaining} days remaining`,
    })

    reason = t('page.planning.dashboard.attention.lowCompletion', {
      rate: Math.round(planning.completionRate),
      staffed: planning.staffedShifts,
      total: planning.totalPossibleShifts,
      defaultValue: `Only ${Math.round(planning.completionRate)}% of shifts are staffed (${planning.staffedShifts}/${planning.totalPossibleShifts})`,
    })
  }

  return (
    <ListItem divider>
      <ListItemText primary={planning.name} secondary={reason} />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        <Chip key={planning._id} label={timeInfo} size="small" color={planning.status === 'active' ? 'success' : 'info'} />
        <Typography variant="caption">
          {dayjs(planning.startDate).format('MMM D')} - {dayjs(planning.endDate).format('MMM D, YYYY')}
        </Typography>
      </Box>
    </ListItem>
  )
}
